import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/common/StatusBadge';
import { STATUS_STEPS, SPECIAL_STATUSES, formatINR } from '../constants/status';

function StatCard({ label, value, icon, highlight }) {
  return (
    <div className="bg-white rounded-lg border border-[#DADCE0] p-4 flex items-start gap-3">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
        highlight ? 'bg-[#E8F0FE] text-[#1A73E8]' : 'bg-[#F1F3F4] text-[#5F6368]'
      }`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-[#5F6368] font-medium">{label}</p>
        <p className={`text-sm font-medium mt-0.5 ${highlight ? 'text-[#1A73E8]' : 'text-[#202124]'}`}>
          {value || '\u2014'}
        </p>
      </div>
    </div>
  );
}

function ActionCard({ to, label, description, icon, color }) {
  return (
    <Link
      to={to}
      className="bg-white rounded-lg border border-[#DADCE0] p-4 hover:shadow-card-hover hover:border-[#D2E3FC] transition-all duration-200 group"
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${color}`}>
        {icon}
      </div>
      <p className="text-sm font-medium text-[#202124] group-hover:text-[#1A73E8] transition-colors">{label}</p>
      <p className="text-xs text-[#5F6368] mt-0.5">{description}</p>
    </Link>
  );
}

function CompactProgress({ currentStatus }) {
  const special = SPECIAL_STATUSES[currentStatus];
  if (special) return null;

  const currentIdx = STATUS_STEPS.findIndex((s) => s.key === currentStatus);
  const progress = STATUS_STEPS.length > 1 ? Math.round((currentIdx / (STATUS_STEPS.length - 1)) * 100) : 0;

  return (
    <div className="bg-white rounded-lg border border-[#DADCE0] p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-[#202124]">Application Progress</h3>
        <span className="text-xs font-medium text-[#1A73E8]">{progress}%</span>
      </div>
      <div className="w-full h-2 bg-[#E8EAED] rounded-full overflow-hidden">
        <div
          className="h-full bg-[#1A73E8] rounded-full transition-all duration-500"
          style={{ width: `${Math.max(progress, 5)}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-[#5F6368]">
          {STATUS_STEPS[currentIdx]?.label || 'Application Received'}
        </span>
        <Link to="/progress" className="text-xs text-[#1A73E8] hover:text-[#1967D2] font-medium">
          View details &rarr;
        </Link>
      </div>
    </div>
  );
}

const WHY_US = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    title: 'Pre-Screening Analysis',
    desc: 'We thoroughly analyze your file and verify every document before applying to any bank.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    title: 'Smart Bank Matching',
    desc: 'We match your profile to the right bank based on your documents, course, and eligibility.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
    title: 'Issue Resolution First',
    desc: 'We identify and resolve potential rejection reasons before submitting your application.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: 'Fast Approval',
    desc: 'Loan approved in as fast as 3 working days with our streamlined process.',
  },
];

const STATS = [
  { number: '100%', label: 'Approval Rate' },
  { number: '3 Days', label: 'Avg. Approval' },
  { number: '50+', label: 'Bank Partners' },
  { number: '10,000+', label: 'Students Helped' },
];

function NewUserHero() {
  return (
    <div className="mb-5 space-y-4">
      {/* CTA Banner */}
      <div className="relative overflow-hidden rounded-lg bg-[#1A73E8] p-6 sm:p-8">
        <div className="relative">
          <h2 className="text-xl sm:text-2xl font-medium text-white leading-tight mb-2">
            Apply Now &mdash; Get Your Loan
            <br className="hidden sm:block" /> Approved in 3 Working Days
          </h2>
          <p className="text-white/80 text-sm leading-relaxed mb-5 max-w-xl">
            GBL Tree is India's most trusted education loan consultancy.
            We don't just apply &mdash; we ensure your loan gets approved.
          </p>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/application"
              className="inline-flex items-center gap-2 bg-white text-[#1A73E8] font-medium rounded-lg px-4 py-2.5 text-sm hover:bg-[#F8F9FA] transition-all"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Fill Loan Application
            </Link>
            <Link
              to="/documents"
              className="inline-flex items-center gap-2 bg-white/15 text-white font-medium rounded-lg px-4 py-2.5 text-sm hover:bg-white/25 transition-all border border-white/30"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload Documents
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {STATS.map((s) => (
          <div key={s.label} className="bg-white rounded-lg border border-[#DADCE0] p-4 text-center">
            <p className="text-xl font-medium text-[#1A73E8]">{s.number}</p>
            <p className="text-xs text-[#5F6368] font-medium mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Why GBL Tree */}
      <div className="bg-white rounded-lg border border-[#DADCE0] p-5">
        <div className="mb-4">
          <h3 className="text-base font-medium text-[#202124]">Why Our Success Rate is 100%</h3>
          <p className="text-xs text-[#5F6368] mt-1">
            Unlike other consultancies, we follow a proven 4-step process that eliminates rejection.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {WHY_US.map((item, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-9 h-9 rounded-full bg-[#E8F0FE] text-[#1A73E8] flex items-center justify-center shrink-0">
                {item.icon}
              </div>
              <div>
                <p className="text-sm font-medium text-[#202124]">{item.title}</p>
                <p className="text-xs text-[#5F6368] mt-0.5 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 p-4 rounded-lg bg-[#E6F4EA] border border-[#CEEAD6]">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[#1E8E3E] flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-[#137333]">The GBL Tree Difference</p>
              <p className="text-xs text-[#137333] mt-0.5 leading-relaxed opacity-80">
                We analyze your complete file, verify every document against bank-specific requirements,
                identify the best-fit bank, and resolve all potential issues before submission.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { student } = useAuth();
  const firstName = student?.name?.split(' ')[0] || 'Student';
  const loanAmount = student?.loanAmount ? formatINR(student.loanAmount) : null;

  const rawStatus = student?.status || 'Documents Pending';
  const displayStatus = rawStatus === 'New Lead' ? 'Documents Pending' : rawStatus;
  const isNewUser = rawStatus === 'New Lead' || rawStatus === 'Documents Pending';

  return (
    <div className="px-4 sm:px-6 py-6 max-w-4xl mx-auto animate-fade-in">
      {/* Welcome */}
      <div className="mb-5">
        <h1 className="text-xl font-normal text-[#202124]">
          {isNewUser ? `Welcome, ${firstName}!` : `Welcome back, ${firstName}`}
        </h1>
        <p className="text-sm text-[#5F6368] mt-1">
          {isNewUser
            ? "Let's get your education loan approved. Here's how to get started."
            : "Here's an overview of your loan application"
          }
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard
          label="Status"
          value={<StatusBadge status={displayStatus} />}
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
        <StatCard label="File Number" value={student?.fileNumber} highlight
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>}
        />
        <StatCard label="Loan Amount" value={loanAmount} highlight
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
        <StatCard
          label="Country / Intake"
          value={student?.country && student?.country !== 'TBD' ? `${student.country}${student.intakeMonth ? ` \u00B7 ${student.intakeMonth}` : ''}` : null}
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
      </div>

      {isNewUser && <NewUserHero />}

      <div className="mb-5">
        <CompactProgress currentStatus={displayStatus} />
      </div>

      {/* Quick actions */}
      <div className="mb-5">
        <h2 className="text-sm font-medium text-[#5F6368] uppercase tracking-wider mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <ActionCard to="/documents" label="Upload Documents" description="Submit required files" color="bg-[#E8F0FE] text-[#1A73E8]"
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>}
          />
          <ActionCard to="/application" label="Loan Form" description="Fill application details" color="bg-[#F3E8FD] text-[#8430CE]"
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
          />
          <ActionCard to="/progress" label="View Progress" description="Track application status" color="bg-[#E6F4EA] text-[#1E8E3E]"
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>}
          />
          <ActionCard to="/profile" label="My Profile" description="View your information" color="bg-[#FEF7E0] text-[#F9AB00]"
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
          />
        </div>
      </div>

      {/* Help card */}
      <div className="bg-[#E8F0FE] rounded-lg border border-[#D2E3FC] p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-[#1A73E8] rounded-full flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-[#174EA6]">Need help?</p>
            <p className="text-xs text-[#1967D2] mt-0.5 leading-relaxed">
              If you have questions about your application or need to update any information,
              please contact your GBL Tree advisor directly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
